import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Fox } from "./Fox.js";

export { Fox, Wordmark } from "./Fox.js";
export type { FoxMood } from "./Fox.js";

type Variant = "primary" | "ghost" | "danger" | "moss";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-fox-deep text-white shadow-[0_4px_0_var(--color-fox-deeper)] hover:bg-fox-deeper active:translate-y-1 active:shadow-none",
  ghost: "border-2 border-line bg-paper-3/60 text-ink hover:border-fox/50 hover:bg-paper-3",
  danger: "bg-rose text-white hover:brightness-95",
  moss: "bg-moss text-white hover:brightness-95",
};

const BASE =
  "inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl px-5 py-3 text-center font-semibold transition-[background-color,box-shadow,transform,border-color] disabled:pointer-events-none disabled:opacity-50";

export function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  disabled,
  autoFocus,
  block = true,
  className = "",
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: Variant;
  disabled?: boolean;
  /** Only where the next step is obvious, e.g. «Weiter» after a wrong answer. */
  autoFocus?: boolean;
  /** Full width on phones; pass false for buttons that sit in a row. */
  block?: boolean;
  className?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      autoFocus={autoFocus}
      className={`${BASE} ${VARIANTS[variant]} ${block ? "w-full" : ""} ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * A link that looks like a button. Needed because a <button> inside an <a> is
 * invalid markup and breaks keyboard activation.
 */
export function ButtonLink({
  children,
  to,
  variant = "primary",
  block = true,
  className = "",
}: {
  children: ReactNode;
  to: string;
  variant?: Variant;
  block?: boolean;
  className?: string;
}) {
  return (
    <Link to={to} className={`${BASE} ${VARIANTS[variant]} ${block ? "w-full" : ""} ${className}`}>
      {children}
    </Link>
  );
}

export function Card({
  children,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  className?: string;
  as?: "div" | "section" | "li";
}) {
  return (
    <Tag
      className={`rounded-3xl border border-line/70 bg-paper-3 shadow-(--shadow-card) ${className}`}
    >
      {children}
    </Tag>
  );
}

/** The label is required: star wording belongs in apps/web/src/copy. */
export function Stars({ n, label }: { n: 0 | 1 | 2 | 3; label: string }) {
  return (
    <span
      role="img"
      className="whitespace-nowrap text-lg tracking-widest text-fox"
      aria-label={label}
    >
      <span aria-hidden>
        {"★".repeat(n)}
        <span className="text-ink-soft/30">{"★".repeat(3 - n)}</span>
      </span>
    </span>
  );
}

/**
 * A word that has been practised but has no star yet: one dot per direction
 * that was already asked. Deliberately not half a star -- a half step is not a
 * step a child can tell apart (docs/STARS.md §8).
 */
export function Started({ n, label }: { n: 0 | 1 | 2; label: string }) {
  return (
    <span role="img" className="flex items-center gap-1 pr-0.5" aria-label={label}>
      {[0, 1].map((i) => (
        <span
          key={i}
          aria-hidden
          className={`h-1.5 w-1.5 rounded-full ${i < n ? "bg-fox/60" : "bg-ink-soft/20"}`}
        />
      ))}
    </span>
  );
}

export function Die({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="none">
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="2" />
      {[
        [8, 8],
        [16, 8],
        [12, 12],
        [8, 16],
        [16, 16],
      ].map(([cx, cy]) => (
        <circle key={`${String(cx)}-${String(cy)}`} cx={cx} cy={cy} r="1.6" fill="currentColor" />
      ))}
    </svg>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-semibold text-ink-soft">{label}</span>
      {children}
      {hint ? <span className="block text-sm text-ink-soft">{hint}</span> : null}
    </label>
  );
}

export function inputClass(): string {
  return "min-h-12 w-full rounded-2xl border-2 border-line bg-paper-3 px-4 py-2.5 text-ink placeholder:text-ink-soft/60 focus:border-fox focus:ring-4 focus:ring-fox/15 focus:outline-none";
}

export function Banner({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="flex items-start gap-2 rounded-2xl border border-rose/30 bg-rose/10 px-4 py-3 text-rose"
    >
      {/* The icon means the message never depends on colour alone. */}
      <span aria-hidden>!</span>
      <span>{children}</span>
    </p>
  );
}

/** Placeholder for "there is nothing here yet" — one of the few spots the fox appears. */
export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-line px-6 py-10 text-center">
      <Fox mood="sleep" className="h-16 w-16" />
      <p className="font-display text-xl">{title}</p>
      {children ? <p className="max-w-sm text-ink-soft">{children}</p> : null}
      {action}
    </div>
  );
}
